import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIZE = 5 * 1024 * 1024;

const PURPOSE_OPTIONS = [
  { value: "hobby", label: "趣味として学びたい", icon: "ri-heart-3-line" },
  { value: "skill", label: "手に職をつけたい", icon: "ri-tools-line" },
  { value: "career", label: "将来の仕事につなげたい", icon: "ri-briefcase-line" },
  { value: "return", label: "復職の準備をしたい", icon: "ri-refresh-line" },
  { value: "change", label: "転職・キャリアチェンジを考えている", icon: "ri-exchange-line" },
  { value: "business", label: "独立・副業の参考にしたい", icon: "ri-store-line" },
  { value: "succession", label: "家業・事業承継に関心がある", icon: "ri-building-4-line" },
  { value: "research", label: "まずは情報収集したい", icon: "ri-search-eye-line" },
  { value: "other", label: "その他", icon: "ri-more-line" },
];

export default function ProfileSettings() {
  const { currentUser, session, refreshProfile } = useApp();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [purpose, setPurpose] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [passwordSection, setPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMsg, setPasswordMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!currentUser?.id) return;
    const { data } = await supabase.from("profiles").select("name, username, bio, avatar_url, purpose").eq("id", currentUser.id).maybeSingle();
    if (!data) return;
    setName(data.name || "");
    setUsername(data.username || "");
    setBio(data.bio || "");
    setPurpose(data.purpose || "");
    setAvatarUrl(data.avatar_url);
  }, [currentUser?.id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser?.id) return;
    setUploadError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError("JPEG / PNG / WebP 形式のみ対応しています。");
      return;
    }
    if (file.size > MAX_SIZE) {
      setUploadError("ファイルサイズは 5MB 以下にしてください。");
      return;
    }
    setUploadingAvatar(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${currentUser.id}/avatar.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
      await supabase.from("profiles").update({ avatar_url: publicUrl }).eq("id", currentUser.id);
      setAvatarUrl(publicUrl);
      await refreshProfile();
    } catch (err) {
      setUploadError(`アップロードに失敗しました: ${(err as Error).message}`);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!currentUser?.id) return;
    setSaving(true);
    const { error } = await supabase.from("profiles").update({ name, username, bio, purpose }).eq("id", currentUser.id);
    if (!error) {
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (!session) return;
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: "error", text: "新しいパスワードが一致しません。" });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: "error", text: "パスワードは8文字以上で設定してください。" });
      return;
    }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPassword(false);
    if (error) {
      setPasswordMsg({ type: "error", text: error.message });
    } else {
      setPasswordMsg({ type: "success", text: "パスワードを変更しました。" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPasswordMsg(null), 4000);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Profile Edit */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <h3 className="font-serif text-deep-green text-lg">プロフィール設定</h3>
          <div className="flex items-center gap-3">
            {saved && <span className="text-xs text-teal font-sans flex items-center gap-1"><i className="ri-checkbox-circle-line" />保存しました</span>}
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap flex items-center gap-1.5">
              {saving ? <><i className="ri-loader-4-line animate-spin" />保存中...</> : "変更を保存"}
            </button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {uploadError && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-start gap-2.5">
              <i className="ri-error-warning-line text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-500 font-sans flex-1">{uploadError}</p>
              <button onClick={() => setUploadError(null)} className="text-red-300 hover:text-red-400 cursor-pointer"><i className="ri-close-line text-sm" /></button>
            </div>
          )}
          {/* Avatar */}
          <div className="flex items-center gap-5">
            <div className="relative w-20 h-20 rounded-full flex-shrink-0">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-stone-100 border-2 border-stone-200">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="アバター" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><i className="ri-user-3-line text-3xl text-charcoal/30" /></div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                    <i className="ri-loader-4-line animate-spin text-white" />
                  </div>
                )}
              </div>
              <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarUpload} className="hidden" />
              <button onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar}
                className="absolute -bottom-1 -right-1 w-7 h-7 flex items-center justify-center bg-teal text-white rounded-full cursor-pointer hover:bg-deep-green transition-colors flex-shrink-0">
                <i className="ri-camera-line text-xs" />
              </button>
            </div>
            <div>
              <p className="text-sm font-sans font-medium text-charcoal">{name || "（未設定）"}</p>
              <p className="text-xs text-charcoal/40 font-sans">@{username || "—"}</p>
              <p className="text-xs text-teal font-sans mt-1 cursor-pointer hover:underline" onClick={() => avatarInputRef.current?.click()}>
                プロフィール画像を変更
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">お名前</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="山田 太郎"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">ユーザー名</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm">@</span>
                <input value={username} onChange={e => setUsername(e.target.value.replace(/\s/g, "").toLowerCase())} placeholder="yamada_taro"
                  className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-sans text-charcoal/60 mb-1.5">自己紹介（任意）</label>
            <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={300} rows={3} placeholder="学習への意気込みや興味のある技術分野など（300文字以内）"
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors" />
            <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{bio.length}/300</p>
          </div>
          {/* Purpose */}
          <div>
            <label className="block text-xs font-sans text-charcoal/60 mb-2">学習の利用目的</label>
            <p className="text-xs text-charcoal/40 font-sans mb-3">あなたに合った講座や事業者を表示するために使用します</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PURPOSE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPurpose(purpose === opt.value ? "" : opt.value)}
                  className={`flex items-center gap-3 w-full text-left px-3.5 py-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${purpose === opt.value ? "border-deep-green bg-deep-green/4" : "border-stone-200 hover:border-teal/40 hover:bg-stone-50"}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${purpose === opt.value ? "bg-deep-green/10" : "bg-stone-100"}`}>
                    <i className={`${opt.icon} text-sm ${purpose === opt.value ? "text-deep-green" : "text-charcoal/50"}`} />
                  </div>
                  <span className={`text-xs font-sans font-medium ${purpose === opt.value ? "text-deep-green" : "text-charcoal"}`}>{opt.label}</span>
                  {purpose === opt.value && (
                    <div className="ml-auto w-4 h-4 flex items-center justify-center rounded-full bg-deep-green flex-shrink-0">
                      <i className="ri-check-line text-white" style={{ fontSize: "10px" }} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
            <p className="text-xs text-charcoal/50 font-sans">
              <i className="ri-information-line text-teal mr-1" />
              メールアドレスの変更は現在サポートしていません。変更が必要な場合はお問い合わせください。
            </p>
          </div>
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <button onClick={() => setPasswordSection(!passwordSection)}
          className="w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-stone-50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-lock-password-line text-deep-green text-xl" />
            </div>
            <div className="text-left">
              <p className="font-sans text-sm font-medium text-charcoal">パスワード変更</p>
              <p className="text-xs text-charcoal/40 font-sans">セキュリティのため定期的な変更を推奨します</p>
            </div>
          </div>
          <i className={`ri-arrow-${passwordSection ? "up" : "down"}-s-line text-charcoal/40 text-xl`} />
        </button>
        {passwordSection && (
          <div className="px-6 pb-6 space-y-4 border-t border-stone-100 pt-5">
            {passwordMsg && (
              <div className={`px-4 py-3 rounded-xl border flex items-center gap-2 text-sm font-sans ${passwordMsg.type === "success" ? "bg-teal/5 border-teal/20 text-teal" : "bg-red-50 border-red-100 text-red-500"}`}>
                <i className={passwordMsg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                {passwordMsg.text}
              </div>
            )}
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">新しいパスワード <span className="text-red-400">*</span></label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="8文字以上"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">新しいパスワード（確認） <span className="text-red-400">*</span></label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="同じパスワードをもう一度"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
            </div>
            <div className="flex justify-end">
              <button onClick={handlePasswordChange} disabled={changingPassword || !newPassword || !confirmPassword}
                className="px-6 py-2.5 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap">
                {changingPassword ? "変更中..." : "パスワードを変更"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
